// copilot generated start 2026-03-16 — EPOCH dynamic pricing: rotating marquee adapter
import { commonSbFixer } from '../../helper/sb-fixer';
import { logAdapterEvent, logError } from '../../helper/logger';
import { IPackageConfig, IGenreSvodEpochOffers } from '../../types/global';

const ADAPTER_NAME = 'RotatingMarqueeAdapter';

export interface IRotatingMarqueeAdapterArgs {
  data: any;
  index: number;
  sbPreviewAllowed?: boolean;
  modalPriorityMap?: Record<string, any>;
  packagesConfig?: IPackageConfig[];
  cmsPageName?: string;
  rsnTierLegalTextObj?: Record<string, any>;
  offerPrices?: IGenreSvodEpochOffers['offerPrices'];
}

const splitPrice = (price: string | null): { dollars: string; cents: string } => {
  if (!price) {
    return {
      dollars: '',
      cents: ''
    };
  }
  const [dollars, cents] = price.split('.');
  return {
    dollars: dollars || '',
    cents: cents ? `${cents}` : ''
  };
};

const buildPricingMap = (
  sliders: any[],
  offerPrices: IGenreSvodEpochOffers['offerPrices'] | undefined,
  packageGroups: string[][]
): Record<number, { strikeThroughPrice: any; offerPrice: any } | null> => {
  const pricingMap: Record<number, { strikeThroughPrice: any; offerPrice: any } | null> = {};

  logAdapterEvent(ADAPTER_NAME, 'offerPrices available keys', {
    count: offerPrices?.length ?? 0,
    keys: offerPrices?.map((po: any) => Object.keys(po)[0]) ?? []
  });

  sliders.forEach((slider: any, idx: number) => {
    const cmsPrices = slider?.prices;
    if (!cmsPrices) {
      pricingMap[idx] = null; return;
    }

    let epochPrice: string | null = null;
    let epochRegularPrice: string | null = null;
    let epochMaxDuration: number | null = null;

    if (offerPrices) {
      const genrePackIds: string[] = packageGroups[idx] || [];
      if (genrePackIds.length) {
        let totalPrice = 0, totalRegularPrice = 0, count = 0, maxDuration = 0;
        let hasRegularPrice = false;
        genrePackIds.forEach((packId: string) => {
          const packData: any = offerPrices.find((po: any) => po[packId])?.[packId];
          if (packData) {
            count += 1;
            const pPrice = parseFloat(packData.price) || 0;
            totalPrice += pPrice;
            const regPrice = parseFloat(packData.regularPrice);
            if (!isNaN(regPrice)) {
              hasRegularPrice = true;
              totalRegularPrice += regPrice;
            } else {
              totalRegularPrice += pPrice;
            }
            const dur = typeof packData.promoDuration === 'number' ? packData.promoDuration : 0;
            if (dur > maxDuration) {
              maxDuration = dur;
            }
          }
        });
        if (count > 0) {
          epochPrice = totalPrice.toFixed(2);
          epochRegularPrice = hasRegularPrice ? totalRegularPrice.toFixed(2) : null;
          epochMaxDuration = maxDuration > 0 ? maxDuration : null;
        }
      }
    }

    const { dollars: offerDollars, cents: offerCents } = splitPrice(epochPrice);
    const offerDuration = !epochMaxDuration || epochMaxDuration <= 0 ?
      '' :
      epochMaxDuration === 1 ?
        'for first mo.' :
        `for first ${epochMaxDuration} mos.`;

    let strikeThroughPrice = null;
    if (cmsPrices.strikeThroughPrice && epochRegularPrice && epochPrice && parseFloat(epochRegularPrice) > parseFloat(epochPrice)) {
      strikeThroughPrice = {
        ...cmsPrices.strikeThroughPrice,
        content: cmsPrices.strikeThroughPrice.content?.replace('[strikeout_price]', epochRegularPrice ?? '')
      };
    } else if (cmsPrices.strikeThroughPrice && !epochPrice) {
      strikeThroughPrice = cmsPrices.strikeThroughPrice;
    }

    let offerPrice = null;
    if (cmsPrices.offerPrice && epochPrice) {
      offerPrice = {
        ...cmsPrices.offerPrice,
        content: cmsPrices.offerPrice.content?.replace('[offer_price]', offerDollars),
        spanText: cmsPrices.offerPrice.spanText ? {
          ...cmsPrices.offerPrice.spanText,
          content: cmsPrices.offerPrice.spanText.content
            ?.replace('[offer_price_cents]', offerCents)
            ?.replace('[offer_duration]', offerDuration)
            ?.trim()
        } : null
      };
    } else if (cmsPrices.offerPrice && !epochPrice) {
      offerPrice = cmsPrices.offerPrice;
    }
    pricingMap[idx] = {
      strikeThroughPrice,
      offerPrice
    };
  });

  return pricingMap;
};

export const rotatingMarqueeAdapter = ({
  data,
  index,
  sbPreviewAllowed,
  modalPriorityMap,
  packagesConfig,
  cmsPageName,
  rsnTierLegalTextObj,
  offerPrices
}: IRotatingMarqueeAdapterArgs): any => {
  try {
    logAdapterEvent(ADAPTER_NAME, 'Adapting data for Rotating Marquee', { cmsPageName });

    const baseComponent = commonSbFixer(
      data,
      index,
      sbPreviewAllowed,
      modalPriorityMap,
      packagesConfig,
      cmsPageName,
      rsnTierLegalTextObj
    );
    const sliders: any[] = baseComponent?.props?.marqueeSliders || [];

    const cmsConfig: any = Array.isArray(baseComponent?.props?.config) ?
      baseComponent.props.config[0] :
      baseComponent?.props?.config;
    const cmsPackageGroups: any[] = Array.isArray(cmsConfig?.packageGroup) ?
      cmsConfig.packageGroup :
      [];
    const packageGroups: string[][] = cmsPackageGroups.map(
      (group: any) => {
        const ids = group?.packageIds;
        if (Array.isArray(ids)) {
          return ids;
        }
        if (typeof ids === 'string') {
          return [ids];
        }
        return [];
      }
    );

    logAdapterEvent(ADAPTER_NAME, 'CMS packageGroups resolved', {
      groupCount: packageGroups.length,
      groups: packageGroups
    });

    const pricingMap = buildPricingMap(sliders, offerPrices, packageGroups);

    if (baseComponent?.props) {
      return {
        ...baseComponent,
        props: {
          ...baseComponent.props,
          pricingMap
        }
      };
    }

    return baseComponent;
  } catch (error) {
    logError(ADAPTER_NAME, error, { cmsPageName });
    return null;
  }
};
// copilot generated end 2026-03-16 — EPOCH dynamic pricing: rotating marquee adapter
